import {z} from 'zod'

import {apiFetch, downloadApiBlob} from '../api/fetch'
import {
    type CreateQuoteInput,
    createQuoteRequestSchema,
    type Quote,
    type QuoteListItem,
    quoteListItemSchema,
    quotePdfExportInputSchema,
    type QuoteRequirementWriteInput,
    quoteRequirementWriteRequestSchema,
    quoteSchema,
    type UpdateQuoteInput,
    updateQuoteRequestSchema,
} from './schemas'

export async function listQuotes(): Promise<QuoteListItem[]> {
    const raw = await apiFetch<unknown>('/quotes')
    return z.array(quoteListItemSchema).parse(raw)
}

export async function getQuote(id: string): Promise<Quote> {
    const raw = await apiFetch<unknown>(`/quotes/${id}`)
    return quoteSchema.parse(raw)
}

export async function downloadQuotePdf(quoteId: string): Promise<void> {
    const input = quotePdfExportInputSchema.parse({quoteId})
    await downloadApiBlob(
        `/quotes/${input.quoteId}/pdf`,
        `quote-${input.quoteId.slice(0, 8).toUpperCase()}.pdf`,
    )
}

export async function createQuote(input: CreateQuoteInput): Promise<Quote> {
    const body = createQuoteRequestSchema.parse(input)
    const raw = await apiFetch<unknown>('/quotes', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return quoteSchema.parse(raw)
}

export async function updateQuote(id: string, input: UpdateQuoteInput): Promise<Quote> {
    const body = updateQuoteRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/quotes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    })
    return quoteSchema.parse(raw)
}

export async function replaceQuoteRequirements(
    id: string,
    requirements: QuoteRequirementWriteInput[],
): Promise<Quote> {
    const body = requirements.map((requirement) =>
        quoteRequirementWriteRequestSchema.parse(requirement),
    )
    const raw = await apiFetch<unknown>(`/quotes/${id}/requirements`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return quoteSchema.parse(raw)
}

export async function emailQuote(quoteId: string): Promise<Quote> {
    const raw = await apiFetch<unknown>(`/quotes/${quoteId}/email`, {method: 'POST'})
    return quoteSchema.parse(raw)
}
